// API Configuration
// Set these in .env for production deployment

const DEFAULT_API_PATH = '/api';
const RUNTIME_API_URL_KEY = 'utms_api_url';

// Late-resolved to avoid circular dependency — resolved at call time, not import time

function normalizeUrl(rawUrl) {
  if (!rawUrl) return rawUrl;
  return rawUrl.replace(/\/$/, '');
}

function isLoopbackHostname(hostname) {
  const value = String(hostname || '').toLowerCase();
  return value === 'localhost' || value === '127.0.0.1' || value === '::1' || value === '[::1]';
}

function ensureApiSuffix(rawUrl) {
  const normalized = normalizeUrl(rawUrl);
  if (!normalized) {
    return normalized;
  }

  if (/\/api$/i.test(normalized)) {
    return normalized;
  }

  return `${normalized}/api`;
}

function readRuntimeApiUrl() {
  if (typeof window === 'undefined') {
    return '';
  }

  try {
    const params = new URLSearchParams(window.location.search);
    const queryApiUrl = params.get('apiUrl');
    if (queryApiUrl) {
      try {
        localStorage.setItem(RUNTIME_API_URL_KEY, queryApiUrl);
      } catch {
        // Ignore storage failures in restricted browser contexts.
      }
      return ensureApiSuffix(queryApiUrl);
    }
  } catch {
    // Ignore malformed query strings.
  }

  try {
    const storedApiUrl = localStorage.getItem(RUNTIME_API_URL_KEY);
    if (storedApiUrl) {
      return ensureApiSuffix(storedApiUrl);
    }
  } catch {
    // Ignore storage access errors in restricted browser contexts.
  }

  return '';
}

function deriveApiUrl() {
  const runtimeApiUrl = readRuntimeApiUrl();
  if (runtimeApiUrl) {
    return runtimeApiUrl;
  }

  if (process.env.REACT_APP_API_URL) {
    return normalizeUrl(process.env.REACT_APP_API_URL);
  }

  if (typeof window !== 'undefined' && window.location?.origin) {
    return `${window.location.origin}/api`;
  }

  return DEFAULT_API_PATH;
}

function ensureSecureWebSocketUrl(rawUrl) {
  if (!rawUrl) return rawUrl;
  try {
    const parsed = new URL(rawUrl);
    if (
      typeof window !== 'undefined' &&
      window.location.protocol === 'https:' &&
      parsed.protocol === 'ws:' &&
      !isLoopbackHostname(parsed.hostname)
    ) {
      parsed.protocol = 'wss:';
    }
    return parsed.toString().replace(/\/$/, '');
  } catch {
    if (typeof window !== 'undefined' && window.location.protocol === 'https:' && rawUrl.startsWith('ws://')) {
      const hostPart = rawUrl.slice('ws://'.length).split(/[/?#]/)[0].split(':')[0];
      if (isLoopbackHostname(hostPart)) {
        return rawUrl;
      }
      return `wss://${rawUrl.slice('ws://'.length)}`;
    }
    return rawUrl;
  }
}

function deriveWsUrl(apiUrl) {
  if (process.env.REACT_APP_WS_URL) {
    return ensureSecureWebSocketUrl(process.env.REACT_APP_WS_URL);
  }

  if (/^https?:\/\//i.test(apiUrl)) {
    const wsFromApi = apiUrl
      .replace(/\/api\/?$/, '')
      .replace(/^http:/i, 'ws:')
      .replace(/^https:/i, 'wss:');
    return ensureSecureWebSocketUrl(wsFromApi);
  }

  if (typeof window !== 'undefined' && window.location?.host) {
    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${wsProtocol}//${window.location.host}`;
  }

  return '';
}

export const API_URL = deriveApiUrl();
export const WS_URL = deriveWsUrl(API_URL);
export const DEFAULT_DRONE_CONNECTION_STRING = process.env.REACT_APP_DEFAULT_DRONE_CONNECTION_STRING || 'udp:DRONE_HOST:14550';

const TOKEN_KEY = 'utms_token';

function getTokenSafe() {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

function clearTokenSafe() {
  try {
    localStorage.removeItem(TOKEN_KEY);
  } catch {
    // Ignore storage access errors in restricted browser contexts.
  }
}

/**
 * Drop-in replacement for fetch() that automatically adds
 * the Authorization: Bearer <token> header for authenticated requests.
 * Also handles 401 responses by clearing the token and dispatching an event.
 *
 * Usage:  import { API_URL, apiFetch } from '../config/api';
 *         const res = await apiFetch(`${API_URL}/drones`);
 */
export async function apiFetch(url, options = {}) {
  const token = getTokenSafe();

  const headers = { ...options.headers };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  // Auto-set Content-Type for JSON body (skip for FormData)
  if (options.body && typeof options.body === 'string' && !headers['Content-Type']) {
    headers['Content-Type'] = 'application/json';
  }

  let response;
  try {
    response = await fetch(url, { ...options, headers });
  } catch (networkError) {
    // Network-level failure (offline, DNS, CORS, etc.)
    _logApiError('API', `Network error: ${networkError.message}`, {
      url,
      method: options.method || 'GET',
      error: networkError.message,
    });
    throw networkError;
  }

  // If 401 on an authenticated request, token is invalid/expired — clear it
  if (response.status === 401 && token) {
    clearTokenSafe();
    window.dispatchEvent(new CustomEvent('auth:unauthorized'));
    _logApiError('API', `Authentication failed (401)`, { url, status: 401 });
  } else if (!response.ok) {
    // Log non-ok responses as warnings (4xx) or errors (5xx)
    const level = response.status >= 500 ? 'error' : 'warn';
    const logger = _getLogger();
    if (logger && logger[level]) {
      logger[level]('API', `HTTP ${response.status}: ${options.method || 'GET'} ${url}`, {
        status: response.status,
        statusText: response.statusText,
      });
    }
  }

  return response;
}

function _getLogger() {
  try {
    // Lazy-resolve at call time to avoid circular dependency at module load
    // eslint-disable-next-line
    const { getGlobalErrorLogger } = require('../contexts/ErrorLogContext');
    return getGlobalErrorLogger();
  } catch {
    return null;
  }
}

function _logApiError(source, message, meta) {
  const logger = _getLogger();
  if (logger && logger.error) {
    logger.error(source, message, meta);
  }
}
